import {
  AreaChartOutlined,
  MedicineBoxOutlined,
  TagsOutlined,
} from "@ant-design/icons"
import { Avatar, Col, Form, Input, Modal, Row, Typography } from "antd"
import dayjs from "dayjs"
import { keyBy, orderBy, uniq } from "lodash"
import { useMemo } from "react"

import { classAtoms, makeupAtoms, tryoutAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import Grid from "#blocks/Grid"
import TagIcons from "#blocks/TagIcons"
import { ALL_SKILL_LEVEL_LABELS } from "#constants/skillLevelConstants"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { MakeupConstructor } from "#customTypes/composedTypes/makeupTypes"
import {
  AddStudentFormValues,
  StudentSkillType,
} from "#customTypes/composedTypes/studentTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import StudentAttendanceList from "#forms/AddStudent/StudentAttendanceList"
import StudentProfileTabs from "#forms/AddStudent/StudentProfileTabs"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useFilteredDocs, useSomeDocs } from "#hooks/jotai/useSubscription"
import { getSessionInfoFromId } from "#utils/classHelpers"
import { STANDARD_DATE_FORMAT } from "#utils/dateHelpers"
import { getCurrentSemester } from "#utils/semesterHelpers"

const { Text } = Typography

const formatSkillLevel = (skillLevel?: string) => {
  if (!skillLevel) return null
  return ALL_SKILL_LEVEL_LABELS[
    skillLevel as keyof typeof ALL_SKILL_LEVEL_LABELS
  ]
}

const MakeupClassPair = ({
  missedClassName,
  makeupClassName,
}: {
  missedClassName: string
  makeupClassName?: string
}) => {
  return (
    <Flex
      col
      gap={6}
      style={{ borderBottom: "1px solid #eee", paddingBottom: 10 }}
    >
      <Flex col gap={0}>
        <Text type="secondary">Missed class</Text>
        <Text>{missedClassName}</Text>
      </Flex>
      <Flex col gap={0}>
        <Text type="secondary">Makeup class</Text>
        <Text>{makeupClassName ?? "not scheduled"}</Text>
      </Flex>
    </Flex>
  )
}

const CurrentSemesterSupplements = ({ studentId }: { studentId: Uuid }) => {
  const semester = useMemo(() => getCurrentSemester(), [])
  const queryConfig: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        studentId: {
          value: studentId,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [studentId]
  )

  const makeups = useFilteredDocs<MakeupConstructor>(
    makeupAtoms,
    DOC_TYPES.MAKEUPS,
    queryConfig
  )
  const tryouts = useFilteredDocs<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    queryConfig
  )

  const supplementalClassIds = useMemo(() => {
    const classIdsFromMakeups = makeups.flatMap((makeup) => [
      makeup.missedClassId,
      makeup.makeupClassId,
    ])
    const classIdsFromTryouts = tryouts.map((tryout) => {
      const [classId] = getSessionInfoFromId(tryout.sessionId)
      return classId
    })
    return uniq([...classIdsFromMakeups, ...classIdsFromTryouts]).filter(
      Boolean
    ) as Uuid[]
  }, [makeups, tryouts])

  const classes = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    supplementalClassIds
  )
  const classesById = useMemo(() => keyBy(classes, "id"), [classes])

  const currentSemesterMakeups = useMemo(() => {
    return orderBy(
      makeups.filter((makeup) => makeup.semester === semester),
      ["createdAt"],
      ["desc"]
    )
  }, [makeups, semester])

  const currentSemesterTryouts = useMemo(() => {
    return orderBy(
      tryouts.filter((tryout) => tryout.semester === semester),
      ["createdAt"],
      ["desc"]
    )
  }, [tryouts, semester])

  return (
    <Flex col gap={10} style={{ marginTop: 30 }}>
      <Text strong>Tryouts</Text>
      {currentSemesterTryouts.length ? (
        <Flex col gap={4}>
          {currentSemesterTryouts.map((tryout) => {
            const [classId] = getSessionInfoFromId(tryout.sessionId)
            return (
              <Flex key={tryout.id} col gap={4}>
                <Text>{classesById[classId]?.name ?? "Unknown class"}</Text>
                <Text type="secondary">
                  {dayjs(tryout.createdAt).isValid()
                    ? dayjs(tryout.createdAt).format(STANDARD_DATE_FORMAT)
                    : "Unknown date"}
                </Text>
              </Flex>
            )
          })}
        </Flex>
      ) : (
        <Text type="secondary">No current semester tryouts found.</Text>
      )}

      <Text strong style={{ marginTop: 30 }}>
        Makeups
      </Text>
      {currentSemesterMakeups.length ? (
        <Flex col gap={20}>
          {currentSemesterMakeups.map((makeup) => (
            <MakeupClassPair
              key={makeup.id}
              missedClassName={
                classesById[makeup.missedClassId]?.name ?? "unknown class"
              }
              makeupClassName={
                makeup.makeupClassId
                  ? (classesById[makeup.makeupClassId]?.name ?? "unknown class")
                  : undefined
              }
            />
          ))}
        </Flex>
      ) : (
        <Text type="secondary">No current semester makeups found.</Text>
      )}
    </Flex>
  )
}

const SimplifiedStudentSummary = ({ studentId }: { studentId: Uuid }) => {
  const student = useGetOneStudent(studentId)

  if (!student) return null

  const currentSemester = getCurrentSemester()
  const semesterSkills = student.skillHistory?.[currentSemester]
  const currentSemesterSkillLevels = [
    formatSkillLevel(semesterSkills?.[StudentSkillType.core]),
    formatSkillLevel(semesterSkills?.[StudentSkillType.digital]),
    formatSkillLevel(semesterSkills?.[StudentSkillType.sculpture]),
  ].filter(Boolean)

  const hasTags = !!student.tags?.length
  const hasAllergies = !!student.allergies?.length

  return (
    <Flex
      col
      gap={10}
      style={{
        borderRight: "1px solid #ccc",
        paddingRight: 20,
        maxHeight: 860,
        overflowY: "auto",
        paddingTop: 40,
      }}
    >
      <Flex col center gap={6} style={{ marginBottom: 8 }}>
        <Avatar
          src={student.avatar ?? "https://placehold.co/64x64"}
          size={96}
          style={{
            border: "1px solid #d9d9d9",
          }}
        >
          {!student.avatar && "No Image"}
        </Avatar>
        <Text strong style={{ fontSize: 18, textAlign: "center" }}>
          {student.name}
        </Text>
        <Text type="secondary" style={{ textAlign: "center" }}>
          {dayjs(student.dob).isValid()
            ? dayjs(student.dob).format(STANDARD_DATE_FORMAT)
            : student.dob}
        </Text>
      </Flex>

      <Flex col gap={6}>
        <Flex gap={8} style={{ alignItems: "flex-start" }}>
          <Flex style={{ alignItems: "center" }} gap={8}>
            <AreaChartOutlined />
            <Text strong>Skill levels:</Text>
          </Flex>
          {currentSemesterSkillLevels.length ? (
            <Flex col gap={0}>
              {currentSemesterSkillLevels.map((item) => (
                <Text key={item}>{item}</Text>
              ))}
            </Flex>
          ) : (
            <Text>None</Text>
          )}
        </Flex>
        <Flex gap={8} style={{ alignItems: "center" }}>
          <Flex style={{ alignItems: "center" }} gap={8}>
            <TagsOutlined />
            <Text strong>Tags:</Text>
          </Flex>
          {hasTags ? <TagIcons values={student.tags} /> : <Text>None</Text>}
        </Flex>
        <Flex gap={8} style={{ alignItems: "center" }}>
          <Flex style={{ alignItems: "center" }} gap={8}>
            <MedicineBoxOutlined />
            <Text strong>Allergies:</Text>
          </Flex>
          {hasAllergies ? (
            <TagIcons values={student.allergies} />
          ) : (
            <Text>None</Text>
          )}
        </Flex>
      </Flex>
      <CurrentSemesterSupplements studentId={studentId} />
    </Flex>
  )
}

const CurrentSemesterData = ({ studentId }: { studentId: Uuid }) => {
  const semester = useMemo(() => getCurrentSemester(), [])

  return (
    <Flex col gap={14}>
      <StudentAttendanceList studentId={studentId} semester={semester} />
    </Flex>
  )
}

const SimplifiedStudentProfileModal = ({
  studentId,
  onClose,
}: {
  studentId: Uuid
  onClose: () => void
}) => {
  const student = useGetOneStudent(studentId)

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={1200}
      title="Student Profile"
      destroyOnClose
    >
      {student ? (
        <Form<AddStudentFormValues> initialValues={student}>
          <Form.Item name="id" style={{ display: "none" }}>
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={32}>
            <Col span={6}>
              <SimplifiedStudentSummary studentId={studentId} />
            </Col>
            <Col span={18}>
              <Grid columns={2} columnGap={24} rowGap={24}>
                <Flex
                  col
                  style={{
                    minWidth: 0,
                    borderRight: "1px solid #eee",
                    paddingRight: 16,
                  }}
                >
                  <CurrentSemesterData studentId={studentId} />
                </Flex>
                <Flex
                  col
                  style={{
                    minWidth: 0,
                    width: 380,
                  }}
                >
                  <StudentProfileTabs
                    showDetails={false}
                    showDocuments={false}
                    showPortfolioButtonInArtworkTab
                  />
                </Flex>
              </Grid>
            </Col>
          </Row>
        </Form>
      ) : (
        <Text type="secondary">Loading student...</Text>
      )}
    </Modal>
  )
}

export default SimplifiedStudentProfileModal
